import { Slot } from '@radix-ui/react-slot'
import { cva, VariantProps } from 'class-variance-authority'
import React from 'react'
import { cn } from '~shared/utils/cn'

const buttonVariants = cva(
  [
    'group',
    'relative',
    'inline-flex items-center justify-center',
    'box-border',
    'appearance-none select-none cursor-pointer',
    'subpixel-antialiased',
    'transition-all duration-300',
    'tap-highlight-transparent',
    'transform-gpu',
    'data-[pressed=true]:scale-[0.97]',
    'opacity-100',
    'text-[16px] leading-[1.2]',
    'disabled:cursor-not-allowed'
  ],
  {
    variants: {
      variant: {
        none: '',
        primary:
          'text-primary bg-tertiary hover:bg-accent disabled:bg-[#C4C4C4]',
        secondary:
          'text-primary bg-accent hover:bg-tertiary disabled:bg-[#C4C4C4]',
        category: '',
        ghost:
          'text-accent bg-[#EBEEFD] hover:bg-[#EBEEFD] hover:text-accent disabled:bg-[#C4C4C4]',
        lead:
          'text-tertiary bg-[#F2F2F2] hover:bg-[#EBEEFD] hover:text-accent disabled:bg-[#C4C4C4]'
      },
      radius: {
        none: '',
        md: 'rounded-[24px]',
        full: 'rounded-full'
      },
      size: {
        none: '',
        sm: 'h-[37px]',
        md: 'h-[55px]',
        slg: 'h-[67px]',
        lg: 'h-[80px]'
      },
      wrap: {
        true: 'whitespace-pre-wrap',
        false: 'whitespace-nowrap min-w-max'
      }
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      radius: 'md'
    }
    // compoundVariants: [],
  }
)

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, wrap = false, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button'
    const { ['aria-controls']: ariaControls, ...restProps } = props
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, wrap }), className)}
        ref={ref}
        {...restProps}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
